// The forecasts: the fetch being shown (the latest unless an earlier one is
// picked), what changed since the fetch before it, each point's forecast, and
// the refresh schedule. Collapsed, it's one line per point saying which
// thresholds are reached, from the server's evaluation of that fetch.
import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, MenuItem, Stack, TextField,
  Tooltip, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SmsIcon from "@mui/icons-material/Sms";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SafetySection from "./SafetySection";
import PointForecast from "./PointForecast";
import RunCommentary from "./RunCommentary";
import ScheduleDialog from "./ScheduleDialog";
import NotifyDialog from "./NotifyDialog";
import {
  BAND_CHIP, BAND_CHIP_VARIANT, BAND_LABEL, SEVERITY_CHIP, SEVERITY_LABEL, VERDICT_LABEL,
  fmtDateTime, fmtMetric, fmtStepTime, metricsByField, profileLabel,
} from "./format";

export function SeverityChip({ severity, prefix = "", ...rest }) {
  if (!severity) return <Chip size="small" label={`${prefix}Not in range yet`} variant="outlined" {...rest} />;
  const s = SEVERITY_CHIP[severity];
  return <Chip size="small" color={s.color} variant={s.variant} label={`${prefix}${SEVERITY_LABEL[severity]}`} {...rest} />;
}

const runLabel = (r) => [
  fmtDateTime(r.fetched_at),
  r.trigger === "scheduled" ? "scheduled" : (r.triggered_by_name || "manual"),
  r.severity ? SEVERITY_LABEL[r.severity] : "not in range",
  r.verdict ? VERDICT_LABEL[r.verdict].toLowerCase() : null,
  r.failed ? `${r.failed} failed` : null,
].filter(Boolean).join(" · ");

export default function ForecastsSection({
  assessment: a, config, onChange, onFetch, fetching, shown, viewRunId, onViewRun, loadingRun,
}) {
  const [scheduling, setScheduling] = useState(false);
  const [alerting, setAlerting] = useState(false);
  const run = shown.run;
  const runs = a.runs || [];
  const index = run ? runs.findIndex(r => r.id === run.id) : -1;
  const viewingOld = viewRunId !== null && runs.length && run && run.id !== runs[0].id;
  const step = (d) => {
    const next = runs[index + d];
    if (next) onViewRun(next.id === runs[0].id ? null : next.id);
  };

  const sched = a.schedule || {};
  const scheduleText = a.auto_refresh
    ? `Auto-refresh: ${profileLabel(config, sched.effective)}${a.refresh_profile ? "" : " (recommended)"}`
      + (a.next_refresh_at ? ` · next ${fmtDateTime(a.next_refresh_at)}` : " · none left before the start")
    : "Auto-refresh off";

  return (
    <SafetySection id="forecasts" title="Forecasts"
      subtitle="Met Office is the primary source and is what the bands and the commentary judge; Windy is shown for comparison."
      actions={
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Button startIcon={<ScheduleIcon />} onClick={() => setScheduling(true)}
                  color={a.auto_refresh ? "primary" : "inherit"}>
            {scheduleText}
          </Button>
          <Button startIcon={<SmsIcon />} onClick={() => setAlerting(true)}
                  color={a.notify_enabled ? "primary" : "inherit"}>
            {a.notify_enabled ? `SMS alerts: ${a.notify_target || "nobody"}` : "SMS alerts off"}
          </Button>
          <Button variant="contained" onClick={onFetch} disabled={fetching || !a.points.length}
                  startIcon={fetching ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}>
            {runs.length ? "Fetch again" : "Fetch forecasts"}
          </Button>
        </Stack>}
      summary={<Summary assessment={a} config={config} run={run} viewingOld={viewingOld} />}>

      {!a.points.length && <Alert severity="info">Add a point above to fetch forecasts for it.</Alert>}
      {a.points.length > 0 && !runs.length && <Alert severity="info">Not fetched yet.</Alert>}

      {runs.length > 0 && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Tooltip title="Earlier fetch">
              <span>
                <IconButton size="small" onClick={() => step(1)} disabled={loadingRun || index >= runs.length - 1}
                            aria-label="Earlier fetch"><ChevronLeftIcon /></IconButton>
              </span>
            </Tooltip>
            <TextField select size="small" label={`Showing fetch ${runs.length - index} of ${runs.length}`}
                       value={run?.id ?? ""} sx={{ flexGrow: 1, maxWidth: 560 }}
                       onChange={e => onViewRun(Number(e.target.value) === runs[0].id ? null : Number(e.target.value))}>
              {runs.map((r, i) => (
                <MenuItem key={r.id} value={r.id}>{i === 0 ? "Latest — " : ""}{runLabel(r)}</MenuItem>
              ))}
            </TextField>
            <Tooltip title="Later fetch">
              <span>
                <IconButton size="small" onClick={() => step(-1)} disabled={loadingRun || index <= 0}
                            aria-label="Later fetch"><ChevronRightIcon /></IconButton>
              </span>
            </Tooltip>
            {loadingRun && <CircularProgress size={18} />}
          </Stack>

          {viewingOld && (
            <Alert severity="info" action={
              <Button color="inherit" size="small" onClick={() => onViewRun(null)}>Show latest</Button>}>
              This is an earlier fetch, as the page showed it then. The latest is{" "}
              {fmtDateTime(runs[0].fetched_at)}.
            </Alert>
          )}

          {run && (
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <SeverityChip severity={run.summary?.severity} prefix="Overall: " />
              <Typography variant="body2" color="text.secondary">
                Fetched {fmtDateTime(run.fetched_at)}
                {run.trigger === "scheduled" ? " by the schedule" : run.triggered_by_name ? ` by ${run.triggered_by_name}` : ""}
                {run.failed ? ` · ${run.failed} provider call${run.failed === 1 ? "" : "s"} failed` : ""}
              </Typography>
            </Stack>
          )}
          {run?.notification && <NotificationLine notification={run.notification} />}
          {run && (run.comparison
            ? <RunCommentary comparison={run.comparison} config={config} />
            : index === runs.length - 1 && (
              <Typography variant="body2" color="text.secondary">The first fetch — nothing to compare it with.</Typography>
            ))}
        </Stack>
      )}

      <Stack spacing={2}>
        {a.points.map(p => (
          <PointForecast key={p.id} point={p} assessment={a} forecasts={shown.forecasts} config={config} />
        ))}
      </Stack>

      <NotifyDialog open={alerting} assessment={a} onClose={() => setAlerting(false)}
                    onSaved={(next) => { onChange(next); setAlerting(false); }} />
      <ScheduleDialog open={scheduling} assessment={a} config={config} onClose={() => setScheduling(false)}
                      onSaved={(next) => { onChange(next); setScheduling(false); }} />
    </SafetySection>
  );
}

// Who this fetch texted (safety/notify.py), or why nobody was.
function NotificationLine({ notification: n }) {
  if (n.skipped) {
    return <Alert severity="warning" icon={<SmsIcon fontSize="inherit" />}>Not texted: {n.skipped}</Alert>;
  }
  const results = n.results || [];
  const ok = results.filter(r => r.sent);
  const failed = results.filter(r => !r.sent);
  return (
    <Alert severity={failed.length ? "warning" : "success"} icon={<SmsIcon fontSize="inherit" />}>
      Texted {n.target}{results.length > 1 ? ` (${ok.length} of ${results.length})` : ""}
      {ok.length && results.length > 1 ? `: ${ok.map(r => r.name).join(", ")}` : ""}.
      {failed.length > 0 && ` Failed for ${failed.map(r => `${r.name} (${r.error})`).join(", ")}.`}
    </Alert>
  );
}

// One line a point: the hazards at or past approaching amber, with the value
// that set each, or that nothing is near.
function Summary({ assessment: a, config, run, viewingOld }) {
  if (!run) {
    return <Typography variant="body2" color="text.secondary">{a.points.length ? "Not fetched yet." : "No points yet."}</Typography>;
  }
  const metrics = metricsByField(config);
  const hazards = Object.fromEntries((config.hazards || []).map(h => [h.key, h]));
  const points = run.summary?.points || [];
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <SeverityChip severity={run.summary?.severity} prefix="Overall: " />
        <Typography variant="body2" color="text.secondary">
          {viewingOld ? "Earlier fetch" : "Latest fetch"}: {fmtDateTime(run.fetched_at)}
          {run.comparison ? ` — ${run.comparison.headline}` : ""}
        </Typography>
      </Stack>
      {points.map(p => {
        const hit = Object.entries(p.hazards || {}).filter(([, h]) => h.band !== "none");
        return (
          <Box key={p.point_id} sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 160 }}>{p.name}</Typography>
            {!p.in_range ? (
              <Typography variant="body2" color="text.secondary">Outside the forecast's range yet</Typography>
            ) : hit.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Nothing near the thresholds</Typography>
            ) : hit.map(([key, h]) => {
              const m = metrics[h.field];
              const v = p.metrics?.[h.field];
              return (
                <Chip key={key} size="small" color={BAND_CHIP[h.band]} variant={BAND_CHIP_VARIANT[h.band] || "filled"}
                      label={`${hazards[key]?.label || key} — ${BAND_LABEL[h.band]}: ${m ? `${m.label.toLowerCase()} ${fmtMetric(m, v?.value)}` : ""}${v?.time ? ` (${fmtStepTime(v.time)})` : ""}`} />
              );
            })}
          </Box>
        );
      })}
    </Stack>
  );
}
